import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatDialogClose } from '@angular/material/dialog'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatToolbar } from '@angular/material/toolbar'
import { MatTooltip } from '@angular/material/tooltip'
import {
  ElderDialogService,
  ElderFormFieldLabelDirective,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSinglePaneWrapperComponent,
  ElderThemeDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { OutputPreviewService } from '../../../output/preview/output-preview.service'
import { ReportSpec } from '../../../output/report/report-job'
import {
  ProductionListBatchContext,
  ProductionListPreviewService,
} from '../../../production/lists/production-list-preview.service'
import { WorkItem } from '../../work-items/work-item'
import { WorkItemTableComponent } from '../../work-items/work-item-table/work-item-table.component'
import {
  WorkBatch,
  WorkBatchAction,
  WorkBatchCommand,
  WorkBatchSegment,
  WorkBatchState,
} from '../work-batch'
import { WorkBatchLineComponent } from '../work-batch-line/work-batch-line.component'
import { WorkBatchService } from '../work-batch.service'
import { isPackageSharingEnabled } from '../../../../../environments/environment'
import { MatSlideToggle } from '@angular/material/slide-toggle'

@Component({
  selector: 'app-work-batch-detail',
  templateUrl: './work-batch-detail.component.html',
  styleUrls: ['./work-batch-detail.component.scss'],
  imports: [
    MatToolbar,
    MatSlideToggle,
    ElderThemeDirective,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    NgTemplateOutlet,
    FormsModule,
    ElderSinglePaneWrapperComponent,
    ElderPaneContentComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneTitleComponent,
    MatButton,
    MatTooltip,
    MatFormField,
    ElderFormFieldLabelDirective,
    MatLabel,
    MatInput,
    WorkBatchLineComponent,
    WorkItemTableComponent,
    ElderHeaderComponent,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
  ],
})
export class WorkBatchDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public WorkBatchState = WorkBatchState
  private dialogService = inject(ElderDialogService)
  private workBatchService = inject(WorkBatchService)
  private toastService = inject(ElderToastService)
  private outputPreviewService = inject(OutputPreviewService)
  private prodListPreviewService = inject(ProductionListPreviewService)
  public readonly batch$ = new BehaviorSubject<WorkBatch>(null)
  isPackageSharingEnabled = isPackageSharingEnabled

  @Input()
  public embedded: boolean

  @Output()
  public readonly batchAbortRequested = new EventEmitter()

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set batch(batch: WorkBatch) {
    this.batch$.next(batch)
  }

  public get batch(): WorkBatch {
    return this.batch$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public abortBatch(event: Event): void {
    const batch = this.batch$.getValue()

    this.batchAbortConfirmDialog().subscribe((abortConfirmed) => {
      if (abortConfirmed) {
        this.sendCommand(batch, WorkBatchAction.ABORT_BATCH)
      }
    })
  }

  public forceAbortBatch(event: Event): void {
    const batch = this.batch$.getValue()

    this.batchAbortConfirmDialog().subscribe((abortConfirmed) => {
      if (abortConfirmed) {
        this.sendCommand(batch, WorkBatchAction.FORCE_ABORT_BATCH)
      }
    })
  }

  public deleteItemsFromBatch(items: WorkItem[]): void {
    const itemIds = items.map((i) => i.id)
    this.sendCommand(this.batch, WorkBatchAction.FORCE_ABORT_BATCH, itemIds)
  }

  public onPreviewBatchLabels(batchId: string, segment: WorkBatchSegment): void {
    this.outputPreviewService.openPreviewDialog(
      ReportSpec.workBatchLabels(batchId, segment.workCenterId)
    )
  }

  public onPreviewItemLabels(workBatchId: string): void {
    this.outputPreviewService.openPreviewDialog(ReportSpec.itemLabelsForBatch(workBatchId))
  }

  public onPreviewPieceLabels(workBatchId: string): void {
    this.outputPreviewService.openPreviewDialog(ReportSpec.itemPieceLabelsForBatch(workBatchId))
  }

  public onPreviewProdList(workBatchId: string): void {
    this.prodListPreviewService.previewProdList(new ProductionListBatchContext(workBatchId, []))
  }

  protected displaySegment(segment: WorkBatchSegment, length: number): string {
    return length > 1 ? ` (${segment.workCenterNumber})` : '' // note the leading space
  }

  protected resetTransferLock() {
    this.sendCommand(this.batch, WorkBatchAction.RESET_TRANSFER_LOCKS)
  }

  protected getSegments(batch: WorkBatch): WorkBatchSegment[] {
    return batch.productionLine.segments
  }

  protected resetEnabled(segments: WorkBatchSegment[]): boolean {
    return segments.filter((segment) => !!segment.transferLock).length > 0
  }

  protected getRelevantLock(segments: WorkBatchSegment[]): Date | undefined {
    return segments
      .map((segment) => segment.transferLock)
      .sort()
      .reverse()
      .find((x) => x) // find first
  }

  isShared(workBatch: WorkBatch): boolean {
    return workBatch.productionLine.packaging.packageSharingEnabled
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private batchAbortConfirmDialog(): Observable<boolean> {
    return this.dialogService.showConfirm({
      title: 'Batch abbrechen?',
      message:
        'Soll dieser Batch wirklich gelöscht werden?\nAlle dazugehörigen Gebinde werden ebenfalls gelöscht!',
      yesNo: true,
    })
  }

  private sendCommand(batch: WorkBatch, action: WorkBatchAction, itemIds: string[] = []) {
    this.workBatchService.sendCommand(new WorkBatchCommand(action, batch.id, itemIds)).subscribe({
      next: (cmdResult) => {
        if (action == WorkBatchAction.ABORT_BATCH || action == WorkBatchAction.FORCE_ABORT_BATCH) {
          this.batchAbortRequested.emit(batch)
          if (cmdResult) {
            const workBatches = cmdResult.workBatches
            this.batch = workBatches && workBatches.length > 1 ? workBatches[0] : null // aborting multiple is not (yet) supported from the frontend
          }
        }
        this.toastService.info('messages.action_successful')
      },
      error: (err) => this.toastService.error('Failed to abort batch ' + batch.id, err),
    })
  }
}
